import type { ColumnDef, OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import { useMemo } from 'react';
import { DateCell, UuidCell } from '@/components/data-table/cells';
import { FilterChips } from '@/components/data-table/column-filter-bar';
import { ColumnFilterHeader } from '@/components/data-table/column-filter-header';
import { DataTable } from '@/components/data-table/data-table';
import { StatusChip } from '@/components/ui/status-chip';
import { type ColumnFilterConfig, useColumnFilters } from '@/hooks/use-column-filters';
import type { AlertSeverity, AlertType, ComplianceAlert } from '@/services/api/compliance-alerts';
import { uniqueSorted } from '@/utils/array';
import { formatDate } from '@/utils/format';
import { formatSnakeCase, shortUuid } from '@/utils/string';
import { getDaysRemainingClass, SEVERITY_CHIP_VARIANTS, STATUS_CHIP_VARIANTS } from './alert-meta';

const PAGE_SIZE = 10;

// Severity is not alphabetically ordered, so both the default sort and the
// header sort control need this rank.
const SEVERITY_RANK: Record<AlertSeverity, number> = { critical: 0, high: 1, medium: 2, low: 3 };

const severityRank = (severity: AlertSeverity): number => SEVERITY_RANK[severity] ?? SEVERITY_RANK.low + 1;

// Legacy filter parity, mapped onto the column-filter engine's kinds: severity /
// event type / status were 'multi-choice' -> 'choice'; pid / alert id were
// 'multi-text' -> substring 'text' (full-UUID paste still matches the shortened
// cell). The engine has no 'date' kind, so date/time filters as text over the
// rendered format; days remaining filters as over/under on the displayed
// (rounded) day count. Status is deliberately unfilterable: the endpoint is
// never queried with `statuses`, so every listed alert is active.
const buildFilterConfigs = (alertType: AlertType): ColumnFilterConfig<ComplianceAlert>[] => [
  { field: 'severity', title: 'Severity', filter: 'choice', getText: (alert) => alert.severity },
  { field: 'date_time', title: 'Date / Time', filter: 'text', getText: (alert) => formatDate(alert.date_time) },
  { field: 'event_type', title: 'Event Type', filter: 'choice', getText: (alert) => formatSnakeCase(alert.event_type) },
  { field: 'pid', title: 'PID', filter: 'text', getText: (alert) => alert.pid },
  { field: 'alert_id', title: 'Alert ID', filter: 'text', getText: (alert) => alert.alert_id },
  ...(alertType === 'onboarding-alert'
    ? [
        {
          field: 'days_remaining',
          title: 'Days Remaining',
          filter: 'number',
          getText: (alert) => String(Math.round(alert.days_remaining ?? 0)),
        } satisfies ColumnFilterConfig<ComplianceAlert>,
      ]
    : []),
];

// Legacy parity: the pid cell opens the user page in a new tab (no copy button);
// only alert_id gets copy-to-clipboard.
const PidCell = ({ pid }: { pid: string }) => (
  <a
    className="inline-flex items-center gap-1 font-mono text-xs underline-offset-2 hover:underline"
    href={`/operations/users/${pid}`}
    onClick={(event) => event.stopPropagation()}
    rel="noreferrer"
    target="_blank"
    title={pid}
  >
    {shortUuid(pid)}
    <ExternalLink className="size-3" />
  </a>
);
PidCell.displayName = 'PidCell';

const DaysRemainingCell = ({ value }: { value: number }) => {
  const days = Math.round(value);
  const isOverdue = value < 0;
  const label = isOverdue ? 'OVERDUE' : `${days} ${days === 1 ? 'Day' : 'Days'}`;
  return <span className={`font-medium ${getDaysRemainingClass(days)}`}>{label}</span>;
};
DaysRemainingCell.displayName = 'DaysRemainingCell';

interface AlertsTableProps {
  alerts: ComplianceAlert[];
  alertType: AlertType;
  isLoading: boolean;
  onRowClick: (alert: ComplianceAlert) => void;
  selectable?: boolean;
  enableSelectAll?: boolean;
  isRowSelectable?: (alert: ComplianceAlert) => boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** Called with the ticked alerts that pass the current filters */
  onSelectionChange?: (alerts: ComplianceAlert[]) => void;
}

export const AlertsTable = ({
  alerts,
  alertType,
  isLoading,
  onRowClick,
  selectable = false,
  enableSelectAll,
  isRowSelectable,
  rowSelection,
  onRowSelectionChange,
  onSelectionChange,
}: AlertsTableProps) => {
  const filterConfigs = useMemo(() => buildFilterConfigs(alertType), [alertType]);
  const filterState = useColumnFilters(alerts, filterConfigs);

  const choiceOptions = useMemo(() => {
    const byField: Record<string, string[]> = {};
    for (const config of filterConfigs) {
      if (config.filter === 'choice') byField[config.field] = uniqueSorted(alerts.map(config.getText).filter(Boolean));
    }
    // Alphabetical order puts low before medium; list severities by rank instead.
    byField.severity?.sort((a, b) => severityRank(a as AlertSeverity) - severityRank(b as AlertSeverity));
    return byField;
  }, [alerts, filterConfigs]);

  // Not memoised: filterState is a fresh object every render, so the header
  // filter controls would defeat any memo (see report-csv-viewer).
  const filterMeta = (field: string) => {
    const config = filterConfigs.find((candidate) => candidate.field === field);
    return config ? { filterControl: <ColumnFilterHeader config={config} options={choiceOptions[config.field]} state={filterState} /> } : undefined;
  };
  const columns: ColumnDef<ComplianceAlert, unknown>[] = [
    {
      accessorKey: 'severity',
      header: 'Severity',
      sortingFn: (rowA, rowB) => severityRank(rowA.original.severity) - severityRank(rowB.original.severity),
      meta: filterMeta('severity'),
      cell: ({ row }) => <StatusChip variant={SEVERITY_CHIP_VARIANTS[row.original.severity] ?? 'muted'}>{row.original.severity}</StatusChip>,
    },
    {
      accessorKey: 'date_time',
      header: 'Date / Time',
      meta: filterMeta('date_time'),
      cell: ({ row }) => <DateCell value={row.original.date_time} />,
    },
    {
      accessorKey: 'event_type',
      header: 'Event Type',
      enableSorting: false,
      meta: filterMeta('event_type'),
      cell: ({ row }) => formatSnakeCase(row.original.event_type),
    },
    {
      accessorKey: 'pid',
      header: 'PID',
      enableSorting: false,
      meta: filterMeta('pid'),
      cell: ({ row }) => <PidCell pid={row.original.pid} />,
    },
    {
      accessorKey: 'alert_id',
      header: 'Alert ID',
      enableSorting: false,
      meta: filterMeta('alert_id'),
      cell: ({ row }) => <UuidCell value={row.original.alert_id} />,
    },
    alertType === 'onboarding-alert'
      ? {
          accessorKey: 'days_remaining',
          header: 'Days Remaining',
          meta: filterMeta('days_remaining'),
          cell: ({ row }) => <DaysRemainingCell value={row.original.days_remaining ?? 0} />,
        }
      : {
          accessorKey: 'status',
          header: 'Status',
          enableSorting: false,
          meta: filterMeta('status'),
          cell: ({ row }) => (row.original.status ? <StatusChip variant={STATUS_CHIP_VARIANTS[row.original.status] ?? 'muted'}>{row.original.status}</StatusChip> : null),
        },
  ];

  return (
    <div className="flex flex-col gap-3">
      <FilterChips clearAll={filterState.clearAll} configs={filterConfigs} filters={filterState.filters} removeFilter={filterState.removeFilter} />
      <DataTable
        columns={columns}
        data={filterState.filteredData}
        // Onboarding alerts arrive pre-sorted most-overdue first (mapAlerts); everything else defaults to severity then newest.
        defaultSorting={
          alertType === 'onboarding-alert'
            ? undefined
            : [
                { id: 'severity', desc: false },
                { id: 'date_time', desc: true },
              ]
        }
        emptyMessage="No results"
        enableRowSelection={selectable && (isRowSelectable ?? true)}
        enableSelectAll={enableSelectAll}
        getRowId={(alert) => alert.alert_id}
        isLoading={isLoading}
        onRowClick={onRowClick}
        onRowSelectionChange={onRowSelectionChange}
        onSelectionChange={onSelectionChange}
        pageSize={PAGE_SIZE}
        rowSelection={rowSelection}
        surface={false}
      />
    </div>
  );
};
AlertsTable.displayName = 'AlertsTable';
